import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

// Utility: default last 30 days range
function getDefaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);

  return {
    fromDate: start.toISOString().split("T")[0],
    toDate: end.toISOString().split("T")[0],
  };
}

export default function useStationwiseTotals(range = null) {
  const [stationData, setStationData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { send, isConnected, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const groupId = localStorage.getItem("group_id");

  const fetchStationTotals = useCallback(async () => {
    if (!mountedRef.current) return;

    setIsLoading(true);
    setError("");

    try {
      const { fromDate, toDate } = range || getDefaultRange();

      const payload = {
        group_id: groupId?.toString(),
        start_date: fromDate,
        end_date: toDate,
      };

      console.log("Payload being sent (station totals):", payload);

      if (!isConnected) {
        console.warn("WebSocket not connected. Returning last known data.");
        return;
      }

      const response = await send("cms_get_stationwise_totals", payload);

      if (!response || response.type !== "cms_get_stationwise_totals_response") {
        throw new Error("Invalid response from server");
      }

      if (!Array.isArray(response.data)) {
        throw new Error("Server did not return valid station data");
      }

      setStationData(response.data); // Overwrite only with valid response
    } catch (err) {
      setError(err.message);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isConnected, send, groupId, range]);

  // Live updates subscription
  useEffect(() => {
    const unregister = registerHandler("station_totals_update", (msg) => {
      if (!mountedRef.current) return;
      if (Array.isArray(msg.data) && msg.data.length > 0) {
        // Merge new data into existing state
        setStationData((prev) => {
          const updatedMap = new Map();
          // Add previous data first
          prev.forEach((item) => updatedMap.set(item.station_id, item));
          // Update with incoming data
          msg.data.forEach((item) => updatedMap.set(item.station_id, item));
          return Array.from(updatedMap.values());
        });
      }
    });

    return () => unregister();
  }, [registerHandler]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchStationTotals();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchStationTotals]);

  return { stationData, isLoading, error, refetch: fetchStationTotals };
}
